import { useEffect, useMemo, useState } from 'react';
import * as Sentry from '@sentry/react';
import { generateGetSignedUrl } from 'api/action';
import { Claim } from 'interfaces';

export type SourceDataStatus =
  | 'idle'
  | 'loading'
  | 'success'
  | 'partial'
  | 'empty'
  | 'error';

export type SourceKind = 'DFT' | 'ORU';
export type OruSuffix = '' | 'F' | 'A' | 'P'; // empty string is to support legacy data without FAP suffix

export interface SourceDataFile {
  kind: SourceKind;
  suffix?: OruSuffix;
  key: string;
  data: unknown;
}

export interface SourceDataMissing {
  kind: SourceKind;
  suffix?: OruSuffix;
  key: string;
  reason: 'not_found';
}

export interface SourceDataFailure {
  kind: SourceKind;
  suffix?: OruSuffix;
  key: string;
  reason: 'error';
  message: string;
}

export interface UseClaimSourceDataResult {
  status: SourceDataStatus;
  files: SourceDataFile[];
  missing: SourceDataMissing[];
  failures: SourceDataFailure[];
}

interface SourceCandidate {
  kind: SourceKind;
  suffix?: OruSuffix;
  key: string;
}

export const buildDftSourceKey = (siteNumber: string, messageID: string) => {
  const normalizedSiteNumber = siteNumber?.trim();
  const normalizedMessageID = messageID?.trim();
  if (!normalizedSiteNumber) {
    throw new Error('siteNumber is required');
  }
  if (!normalizedMessageID) {
    throw new Error('messageID is required');
  }

  return `${normalizedSiteNumber}/dft/${normalizedMessageID}.json`;
};

export const buildOruSourceKey = (
  siteNumber: string,
  messageID: string,
  suffix: OruSuffix
) => {
  const normalizedSiteNumber = siteNumber?.trim();
  const normalizedMessageID = messageID?.trim();
  if (!normalizedSiteNumber) {
    throw new Error('siteNumber is required');
  }
  if (!normalizedMessageID) {
    throw new Error('messageID is required');
  }

  return `${normalizedSiteNumber}/oru/${normalizedMessageID}${suffix}.json`;
};

const ORU_SUFFIXES: OruSuffix[] = ['', 'F', 'A', 'P'];

const isMissingSourceError = (error: unknown): boolean => {
  if (!(error instanceof Error)) {
    return false;
  }
  const message = error.message.toLowerCase();
  return (
    message.includes('404') ||
    message.includes('not found') ||
    message.includes('nosuchkey') ||
    message.includes('no such key')
  );
};

const formatErrorMessage = (error: unknown): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return 'Unknown source-data error';
};

const fetchJsonSource = async (bucket: string, candidate: SourceCandidate) => {
  const signedUrl = await generateGetSignedUrl({
    bucket,
    key: encodeURIComponent(candidate.key),
  });

  const response = await fetch(signedUrl);
  if (!response.ok) {
    const text = await response.text();
    throw new Error(
      `Failed to fetch ${candidate.key}: ${response.status} ${response.statusText} ${text}`
    );
  }

  const text = await response.text();
  try {
    return JSON.parse(text);
  } catch {
    throw new Error(`Invalid JSON in source file: ${candidate.key}`);
  }
};

const toDisplayValue = (value: unknown): string | null => {
  if (value == null) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return null;
};

const pickValue = (data: any, paths: string[]): string | null => {
  for (const path of paths) {
    const value = path
      .split('.')
      .reduce<any>((acc, key) => (acc ? acc[key] : undefined), data);
    const normalized = toDisplayValue(value);
    if (normalized) return normalized;
  }
  return null;
};

export interface SourceDataField {
  label: string;
  value: string;
}

export interface FormattedSourceData {
  summary: SourceDataField[];
  patient: SourceDataField[];
  visit: SourceDataField[];
  provider: SourceDataField[];
}

export const formatClaimSourceData = (
  claim: Claim | null,
  files: SourceDataFile[]
): FormattedSourceData => {
  const dft = files.find((file) => file.kind === 'DFT')?.data as any;
  const oruF = files.find((file) => file.kind === 'ORU' && file.suffix === 'F')
    ?.data as any;
  const oruA = files.find((file) => file.kind === 'ORU' && file.suffix === 'A')
    ?.data as any;
  const oruP = files.find((file) => file.kind === 'ORU' && file.suffix === 'P')
    ?.data as any;
  const oru = oruF ?? oruA ?? oruP;

  const summary: SourceDataField[] = [];
  const patient: SourceDataField[] = [];
  const visit: SourceDataField[] = [];
  const provider: SourceDataField[] = [];

  const pushField = (
    fields: SourceDataField[],
    label: string,
    value: string | null
  ) => {
    if (value) {
      fields.push({ label, value });
    }
  };

  pushField(summary, 'Message ID', claim?.messageID ?? null);
  pushField(summary, 'Site Number', claim?.site?.siteNumber ?? null);
  pushField(
    summary,
    'Patient Name',
    pickValue(dft ?? oru, ['PID.PID.5.PID.5.1'])
  );
  pushField(
    summary,
    'Date of Birth',
    pickValue(dft ?? oru, ['PID.PID.7.PID.7.1'])
  );
  pushField(
    summary,
    'Health Card / PHIN',
    pickValue(dft ?? oru, ['PID.PID.19.PID.19.1'])
  );
  pushField(
    summary,
    'Service Date',
    pickValue(dft ?? oru, ['PV1.PV1.44.PV1.44.1', 'FT1.0.FT1.4.FT1.4.1'])
  );

  pushField(
    patient,
    'Patient Account Number / MRN',
    pickValue(dft ?? oru, ['PID.PID.3.PID.3.1'])
  );
  pushField(patient, 'Sex', pickValue(dft ?? oru, ['PID.PID.8.PID.8.1']));
  pushField(patient, 'Address', pickValue(dft ?? oru, ['PID.PID.11.PID.11.1']));
  pushField(
    patient,
    'Phone Number',
    pickValue(dft ?? oru, ['PID.PID.13.PID.13.1'])
  );

  pushField(
    visit,
    'Visit Number',
    pickValue(dft ?? oru, ['PV1.PV1.19.PV1.19.1'])
  );
  pushField(
    visit,
    'Encounter Date',
    pickValue(dft ?? oru, ['PV1.PV1.44.PV1.44.1'])
  );
  pushField(
    visit,
    'Location / Clinic',
    pickValue(dft ?? oru, ['PV1.PV1.3.PV1.3.1'])
  );

  pushField(
    provider,
    'Attending Provider',
    pickValue(dft ?? oru, ['PV1.PV1.7.PV1.7.1', 'OBR.0.OBR.16.OBR.16.1'])
  );
  pushField(
    provider,
    'Ordering Provider',
    pickValue(oru, ['OBR.0.OBR.16.OBR.16.1'])
  );
  pushField(
    provider,
    'Billing Provider',
    pickValue(dft, ['FT1.0.FT1.20.FT1.20.1'])
  );

  return {
    summary,
    patient,
    visit,
    provider,
  };
};

export const useClaimSourceData = (
  open: boolean,
  claim: Claim | null
): UseClaimSourceDataResult => {
  const [status, setStatus] = useState<SourceDataStatus>('idle');
  const [files, setFiles] = useState<SourceDataFile[]>([]);
  const [missing, setMissing] = useState<SourceDataMissing[]>([]);
  const [failures, setFailures] = useState<SourceDataFailure[]>([]);

  const bucket = useMemo(() => {
    return `hl7-message-files-${import.meta.env.VITE_ACCOUNT_ID}`;
  }, []);

  useEffect(() => {
    if (!open) {
      setStatus('idle');
      setFiles([]);
      setMissing([]);
      setFailures([]);
      return;
    }

    const messageID = claim?.messageID?.trim();
    const siteNumber = claim?.site?.siteNumber?.trim();

    if (!messageID || !siteNumber) {
      setStatus('empty');
      setFiles([]);
      setMissing([]);
      setFailures([]);
      return;
    }

    const candidates: SourceCandidate[] = [
      {
        kind: 'DFT',
        key: buildDftSourceKey(siteNumber, messageID),
      },
      ...ORU_SUFFIXES.map((suffix) => ({
        kind: 'ORU' as const,
        suffix,
        key: buildOruSourceKey(siteNumber, messageID, suffix),
      })),
    ];

    let cancelled = false;

    const run = async () => {
      setStatus('loading');
      setFiles([]);
      setMissing([]);
      setFailures([]);

      const settled = await Promise.allSettled(
        candidates.map(async (candidate) => {
          const data = await fetchJsonSource(bucket, candidate);
          return { candidate, data };
        })
      );

      if (cancelled) return;

      const nextFiles: SourceDataFile[] = [];
      const nextMissing: SourceDataMissing[] = [];
      const nextFailures: SourceDataFailure[] = [];

      settled.forEach((result, index) => {
        const candidate = candidates[index];
        if (result.status === 'fulfilled') {
          nextFiles.push({
            kind: candidate.kind,
            suffix: candidate.suffix,
            key: candidate.key,
            data: result.value.data,
          });
          return;
        }

        const error = result.reason;
        if (isMissingSourceError(error)) {
          nextMissing.push({
            kind: candidate.kind,
            suffix: candidate.suffix,
            key: candidate.key,
            reason: 'not_found',
          });
          return;
        }

        const message = formatErrorMessage(error);
        nextFailures.push({
          kind: candidate.kind,
          suffix: candidate.suffix,
          key: candidate.key,
          reason: 'error',
          message,
        });

        Sentry.withScope((scope) => {
          scope.setLevel('error');
          scope.setContext('claimSourceData', {
            claimID: claim?.id,
            messageID,
            siteNumber,
            kind: candidate.kind,
            suffix: candidate.suffix,
            key: candidate.key,
            reason: message,
          });
          Sentry.captureException(
            new Error('Failed to load claim source data')
          );
        });
      });

      setFiles(nextFiles);
      setMissing(nextMissing);
      setFailures(nextFailures);

      if (nextFiles.length === 0 && nextFailures.length > 0) {
        setStatus('error');
      } else if (nextFiles.length === 0) {
        setStatus('empty');
      } else if (nextFailures.length > 0) {
        setStatus('partial');
      } else {
        setStatus('success');
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [bucket, claim, open]);

  return {
    status,
    files,
    missing,
    failures,
  };
};
